import { User } from "firebase/auth";
import React, { useEffect, useState, useRef, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip,
  ResponsiveContainer, CartesianGrid, Cell,
} from "recharts";
import { useBreadcrumbs } from "../context/BreadcrumbContext";
import {
  getScorePredictor,
  savePrediction,
} from "../api/scorePredictor";
import { fetchLeague } from "../api/leagues";
import PageLoader from "../components/PageLoader";
import ClubBadge from "../components/ClubBadge";
import { getClubBadge } from "../data/clubBadges";
import StatusDot from "../components/StatusDot";
import { useExportImage } from "../hooks/useExportImage";
import { safeFilename } from "../utils/shareLeague";
import { card, widePageContainer, secondaryBtn, primaryBtn, exportBtn, selectStyle, panelHeading } from "../styles";
import StatBox from "../components/league/StatBox";
import FormBar, { FormBarEntry } from "../components/league/FormBar";
import { useIsMobile } from "../hooks/useIsMobile";

interface Props { user: User; }

function formatKickoff(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

function resultLabel(
  pred: { homeScore: number; awayScore: number },
  actual: { homeScore: number; awayScore: number },
  exactPts: number,
  correctPts: number,
): { label: string; pts: number; color: string } {
  if (pred.homeScore === actual.homeScore && pred.awayScore === actual.awayScore) {
    return { label: "Exact score", pts: exactPts, color: "var(--color-success)" };
  }
  if (Math.sign(pred.homeScore - pred.awayScore) === Math.sign(actual.homeScore - actual.awayScore)) {
    return { label: "Correct result", pts: correctPts, color: "oklch(0.72 0.16 80)" };
  }
  return { label: "Wrong result", pts: 0, color: "var(--color-text-muted)" };
}

const SCORE_OPTIONS = Array.from({ length: 11 }, (_, i) => i);

function ScoreSelect({ value, onChange, compact }: { value: number | null; onChange: (v: number | null) => void; compact?: boolean }) {
  return (
    <select
      value={value === null ? "" : value}
      onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))}
      style={{
        ...selectStyle,
        width: compact ? 52 : 68,
        padding: compact ? "0.15rem 1rem 0.15rem 0.35rem" : "0.2rem 1.6rem 0.2rem 0.5rem",
        fontSize: "1.05rem",
        fontFamily: "var(--font-display)",
        fontWeight: 800,
        letterSpacing: "0.03em",
        borderRadius: 8,
        textAlign: "center",
      }}
    >
      <option value="">–</option>
      {SCORE_OPTIONS.map((n) => <option key={n} value={n}>{n}</option>)}
    </select>
  );
}

const tooltipStyle = {
  background: "var(--color-surface)",
  border: "1px solid var(--color-border)",
  borderRadius: 8,
  fontSize: "0.78rem",
};

function SpBarTooltip({ active, payload }: any) {
  const { t } = useTranslation();
  if (!active || !payload?.length) return null;
  const d = payload[0].payload as { name: string; score: number };
  return (
    <div style={{ ...tooltipStyle, padding: "0.4rem 0.7rem" }}>
      <div style={{ fontWeight: 700, color: "var(--color-text)" }}>{d.name}</div>
      <div style={{ color: d.score > 0 ? "var(--color-success)" : "var(--color-text-dim)" }}>
        {d.score} {d.score === 1 ? t("scorePredictor.point") : t("scorePredictor.points")}
      </div>
    </div>
  );
}

export default function ScorePredictorPage({ user }: Props) {
  const { id: leagueId, memberId: viewMemberId } = useParams<{ id: string; memberId?: string }>();
  const isViewingMember = !!viewMemberId;
  const { t } = useTranslation();
  const isMobile = useIsMobile();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [leagueName, setLeagueName] = useState("");
  const [leagueStatus, setLeagueStatus] = useState<"PREPARING" | "RUNNING" | "COMPLETED" | null>(null);
  const [leagueSeason, setLeagueSeason] = useState("");
  const [data, setData] = useState<Awaited<ReturnType<typeof getScorePredictor>> | null>(null);
  const [selectedGwId, setSelectedGwId] = useState<string | null>(null);

  const [pendingScores, setPendingScores] = useState<Record<string, { home: number | null; away: number | null }>>({});
  const [savingGw, setSavingGw] = useState(false);

  const [activeTab, setActiveTab] = useState<"selections" | "performance">("selections");
  const [chartTab, setChartTab] = useState<"per-gw" | "cumulative">("per-gw");
  const [shareDropdownOpen, setShareDropdownOpen] = useState(false);

  const exportHeaderRef = useRef<HTMLDivElement>(null);
  const gwListRef = useRef<HTMLDivElement>(null);
  const gwItemRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
  const { status: exportStatus, loading: shareLoading, runExport } = useExportImage();

  const breadcrumbLabel = isViewingMember && data?.viewMemberDisplayName
    ? `${data.viewMemberDisplayName}'s ${t("scorePredictor.predictions")}`
    : t("scorePredictor.myPredictions");
  useBreadcrumbs(
    leagueName
      ? [{ label: t("common.home"), to: "/overview" }, { label: leagueName, to: `/leagues/${leagueId}` }, { label: breadcrumbLabel }]
      : [{ label: t("common.home"), to: "/overview" }],
  );

  const load = useCallback(async (gwId?: string) => {
    if (!leagueId) return;
    try {
      const result = await getScorePredictor(user, leagueId, gwId, viewMemberId);
      setData(result);
      setLeagueStatus(result.leagueStatus ?? null);
      setSelectedGwId(result.selectedGameweekId);
      const pending: Record<string, { home: number | null; away: number | null }> = {};
      for (const f of result.fixtures) {
        if (f.myPrediction && !f.locked) {
          pending[f.id] = { home: f.myPrediction.homeScore, away: f.myPrediction.awayScore };
        }
      }
      setPendingScores(pending);
    } catch {
      setError(t("common.somethingWentWrong"));
    } finally {
      setLoading(false);
    }
  }, [leagueId, user, t, viewMemberId]);

  useEffect(() => {
    if (!leagueId) return;
    fetchLeague(user, leagueId)
      .then((l) => {
        setLeagueName(l.name);
        setLeagueSeason((l as any).competition?.season ?? "");
      })
      .catch(() => {});
  }, [leagueId, user]);

  useEffect(() => { load(); }, [load]);

  const selectGw = async (gwId: string) => {
    if (gwId === selectedGwId) return;
    setSelectedGwId(gwId);
    setLoading(true);
    await load(gwId);
  };

  useEffect(() => {
    if (!selectedGwId || !gwListRef.current) return;
    const el = gwItemRefs.current.get(selectedGwId);
    if (!el) return;
    requestAnimationFrame(() => {
      const container = gwListRef.current;
      if (!container) return;
      const containerRect = container.getBoundingClientRect();
      const elRect = el.getBoundingClientRect();
      const relativeTop = elRect.top - containerRect.top + container.scrollTop;
      container.scrollTop = Math.max(0, relativeTop - el.offsetHeight * 3);
    });
  }, [selectedGwId, data]);

  const handleSaveGw = async () => {
    if (!leagueId || savingGw) return;
    setSavingGw(true);
    try {
      await Promise.all(
        (data?.fixtures ?? [])
          .filter((f) => !f.locked)
          .map((f) => {
            const s = pendingScores[f.id];
            if (!s || s.home === null || s.away === null) return Promise.resolve();
            return savePrediction(user, leagueId, f.id, s.home, s.away);
          })
      );
      await load(selectedGwId ?? undefined);
    } catch {
      // stay on form
    } finally {
      setSavingGw(false);
    }
  };

  const handleExport = async (action: "download" | "copy") => {
    setShareDropdownOpen(false);
    await runExport([exportHeaderRef.current], action, `${safeFilename(leagueName || "predictions")}.png`);
  };

  if (loading && !data) return <PageLoader />;
  if (error || !data) return <div style={{ padding: "2rem", color: "var(--color-text-muted)" }}>{error ?? t("common.somethingWentWrong")}</div>;

  const SP_EXACT = 3;
  const SP_CORRECT = 1;
  const { gameweeks, fixtures } = data;

  // In member view, only show completed GWs (no upcoming predictions visible)
  const visibleGameweeks = isViewingMember ? gameweeks.filter((g) => !!g.completedAt) : gameweeks;

  const completedGws = gameweeks.filter((g) => !!g.completedAt).sort((a, b) => a.number - b.number);
  const totalScore = completedGws.reduce((s, g) => s + g.myScore, 0);
  const prevGwScore = completedGws[completedGws.length - 1]?.myScore ?? 0;
  const avgPerGw = completedGws.length > 0 ? totalScore / completedGws.length : null;
  const scoreHistory: FormBarEntry[] = completedGws.map((g) => ({ gwNumber: g.number, gwName: g.name, score: g.myScore }));

  const perGwData = completedGws.map((g) => ({ name: g.shortName ?? g.name, score: g.myScore }));
  let cumScore = 0;
  const cumulativeData = [
    { name: t("scorePredictor.startLabel"), score: 0 },
    ...completedGws.map((g) => { cumScore += g.myScore; return { name: g.shortName ?? g.name, score: cumScore }; }),
  ];
  const tickInterval = perGwData.length > 8 ? Math.floor(perGwData.length / 5) : 0;

  return (
    <div className="sg-fade-in" style={{ ...widePageContainer, padding: "1.5rem 0" }}>

      {/* ── Header card ── */}
      <div ref={exportHeaderRef} style={{ ...card, marginBottom: "1.25rem" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.5rem", flexWrap: "wrap" }}>
          {[t("scorePredictor.predictions"), leagueSeason].filter(Boolean).map((s, i) => (
            <React.Fragment key={i}>
              <span style={{ fontSize: "0.62rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: "var(--color-text-dim)" }}>{s}</span>
              <span style={{ color: "var(--color-border)", fontSize: "0.75rem" }}>·</span>
            </React.Fragment>
          ))}
          {leagueStatus && <StatusDot status={leagueStatus} />}
        </div>

        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "1rem", marginBottom: "0.4rem", flexWrap: "wrap" }}>
          <div style={{ minWidth: 0 }}>
            <h1 style={{ margin: "0 0 0.25rem", fontFamily: "var(--font-display)", fontSize: "1.75rem", fontWeight: 800, color: "var(--color-text)" }}>
              {isViewingMember && data.viewMemberDisplayName
                ? `${data.viewMemberDisplayName}${data.viewMemberDisplayName.endsWith("s") ? "'" : "'s"} ${t("scorePredictor.predictions")}`
                : user.displayName
                ? `${user.displayName}${user.displayName.endsWith("s") ? "'" : "'s"} ${t("scorePredictor.predictions")}`
                : t("scorePredictor.myPredictions")}
            </h1>
            <p style={{ margin: 0, fontSize: "0.82rem", color: "var(--color-text-muted)" }}>
              {t("scorePredictor.earnPrefix")} <strong style={{ color: "var(--color-accent)" }}>{SP_EXACT} {t("scorePredictor.points")}</strong> {t("scorePredictor.earnExactSuffix")} · <strong style={{ color: "var(--color-accent)" }}>{SP_CORRECT} {t("scorePredictor.point")}</strong> {t("scorePredictor.earnCorrectSuffix")}
            </p>
          </div>
          <div data-no-export style={{ display: "flex", gap: "0.5rem", alignItems: "center", flexShrink: 0 }}>
            {!isViewingMember && gameweeks.length > 0 && (
              <div style={{ position: "relative" }}>
                <button
                  style={exportBtn}
                  onClick={() => setShareDropdownOpen((v) => !v)}
                  disabled={shareLoading}
                  title={t("scorePredictor.exportBtn")}
                >
                  <svg width="13" height="13" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 2v11M6 9l4 4 4-4"/><path d="M3 14v2a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-2"/></svg>
                </button>
                {shareDropdownOpen && (
                  <>
                    <div style={{ position: "fixed", inset: 0, zIndex: 99 }} onClick={() => setShareDropdownOpen(false)} />
                    <div style={{ position: "absolute", right: 0, top: "calc(100% + 6px)", background: "var(--color-surface)", border: "1px solid var(--color-border)", borderRadius: 10, boxShadow: "0 8px 32px var(--color-shadow)", zIndex: 100, minWidth: 170, overflow: "hidden" }}>
                      {(["download", "copy"] as const).map((action) => (
                        <button key={action} onClick={() => handleExport(action)} style={{ display: "block", width: "100%", padding: "0.5rem 1rem", background: "none", border: "none", textAlign: "left", fontSize: "0.85rem", color: "var(--color-text)", cursor: "pointer" }}>
                          {action === "download" ? t("scorePredictor.downloadImage") : t("scorePredictor.copyImage")}
                        </button>
                      ))}
                    </div>
                  </>
                )}
                {exportStatus && (
                  <div style={{ position: "absolute", right: 0, top: "calc(100% + 6px)", zIndex: 100, whiteSpace: "nowrap", padding: "0.4rem 0.7rem", borderRadius: 8, fontSize: "0.78rem", fontWeight: 600, background: "var(--color-surface)", border: `1px solid ${exportStatus.type === "error" ? "var(--color-danger)" : "var(--color-border)"}`, color: exportStatus.type === "error" ? "var(--color-danger)" : "var(--color-text)", boxShadow: "0 8px 32px var(--color-shadow)" }}>
                    {exportStatus.message}
                  </div>
                )}
              </div>
            )}
            <Link to={`/leagues/${leagueId}`} style={{ ...secondaryBtn, textDecoration: "none" }}>{t("scorePredictor.backToLeague")}</Link>
          </div>
        </div>

        <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap", marginTop: "1rem" }}>
          <StatBox label={t("scorePredictor.scoreLabel")} value={totalScore} sub={t("scorePredictor.totalPoints")} accent borderColor="var(--color-accent)" />
          <StatBox label={t("scorePredictor.prevGwLabel")} value={prevGwScore} sub={t("scorePredictor.pointsLastGw")} mobileHide borderColor="var(--color-accent)" />
          {data.leagueGwAverage != null && completedGws.length > 0 && (() => {
            const diff = prevGwScore - data.leagueGwAverage;
            const diffNode = diff > 0
              ? <span style={{ color: "var(--color-success)" }}>{`+${diff.toFixed(1)}`}</span>
              : diff < 0
                ? <span style={{ color: "var(--color-danger)" }}>{diff.toFixed(1)}</span>
                : <span>0</span>;
            return <StatBox label={t("scorePredictor.vsGwAvgLabel")} value={diffNode} sub={t("scorePredictor.lastGwVsLeague")} mobileHide borderColor="var(--color-accent)" />;
          })()}
          {scoreHistory.length > 0 && (
            <StatBox label={t("scorePredictor.formLabel")} value={<FormBar history={scoreHistory} />} sub="" borderColor="var(--color-accent)" />
          )}
          {avgPerGw !== null && (
            <StatBox label={t("scorePredictor.avgPerGwLabel")} value={avgPerGw.toFixed(1)} sub={t("scorePredictor.pointsPerGw")} borderColor="var(--color-accent)" />
          )}
        </div>
      </div>

      {/* ── Tab bar ── */}
      <div style={{ display: "flex", marginBottom: "1.25rem", border: "1px solid var(--color-border)", borderRadius: 10, overflow: "hidden" }}>
        {(["selections", "performance"] as const).map((tab, idx) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            style={{
              flex: 1, padding: "0.65rem 1rem", borderRadius: 0, fontSize: "0.85rem",
              fontWeight: activeTab === tab ? 700 : 500, cursor: "pointer", fontFamily: "var(--font-body)",
              border: "none", borderRight: idx === 0 ? "1px solid var(--color-border)" : "none",
              background: activeTab === tab ? "var(--color-accent)" : "transparent",
              color: activeTab === tab ? "var(--color-btn-primary-text)" : "var(--color-text-muted)",
              transition: "background 0.15s, color 0.15s",
            }}
          >
            {tab === "selections" ? t("scorePredictor.selectionsTab") : t("scorePredictor.performanceTab")}
          </button>
        ))}
      </div>

      {/* ── SELECTIONS TAB ── */}
      {activeTab === "selections" && (
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "220px 1fr", gap: "1.25rem", alignItems: "start" }}>

          {/* GW list */}
          {(() => {
            const selectedGwIdx = visibleGameweeks.findIndex((g) => g.id === selectedGwId);
            const firstUpcomingIdx = visibleGameweeks.findIndex((g) => !g.completedAt);
            const navBtnStyle: React.CSSProperties = {
              flex: 1, background: "none", border: "none", borderRadius: 0,
              padding: "0.55rem", fontSize: "0.78rem", fontWeight: 600,
              cursor: "pointer", color: "var(--color-accent)",
              display: "flex", alignItems: "center", justifyContent: "center", gap: "0.25rem",
            };
            return (
              <div style={{ ...card, padding: 0, overflow: "hidden", display: "flex", flexDirection: "column" }}>
                {visibleGameweeks.length === 0 && (
                  <div style={{ padding: "1rem", color: "var(--color-text-muted)", fontSize: "0.85rem" }}>{t("scorePredictor.noGameweeks")}</div>
                )}
                <div ref={gwListRef} style={{ maxHeight: isMobile ? 240 : 520, overflowY: "auto", flex: 1 }}>
                  {visibleGameweeks.map((gw, idx) => {
                    const isSelected = gw.id === selectedGwId;
                    const isCompleted = !!gw.completedAt;
                    const isNextUp = idx === firstUpcomingIdx;
                    return (
                      <React.Fragment key={gw.id}>
                        {isNextUp && firstUpcomingIdx > 0 && (
                          <div style={{
                            padding: "0.3rem 1rem",
                            background: "color-mix(in srgb, var(--color-accent) 8%, transparent)",
                            borderTop: "1px solid var(--color-accent)",
                            borderBottom: "1px solid var(--color-accent)",
                            fontSize: "0.55rem", fontWeight: 700, textTransform: "uppercase",
                            letterSpacing: "0.1em", color: "var(--color-accent)",
                          }}>
                            {t("scorePredictor.upcoming")}
                          </div>
                        )}
                        <button
                          ref={(el) => { if (el) gwItemRefs.current.set(gw.id, el); else gwItemRefs.current.delete(gw.id); }}
                          onClick={() => selectGw(gw.id)}
                          style={{
                            display: "flex", alignItems: "center", justifyContent: "space-between",
                            gap: "0.5rem", width: "100%", padding: "0.75rem 1rem",
                            background: isSelected
                              ? "var(--color-accent-glow)"
                              : isCompleted
                              ? "color-mix(in srgb, var(--color-success) 6%, transparent)"
                              : isNextUp
                              ? "color-mix(in srgb, var(--color-accent) 4%, transparent)"
                              : "none",
                            border: "none",
                            borderBottom: "1px solid var(--color-border-subtle)",
                            borderLeft: `3px solid ${isSelected ? "var(--color-accent)" : isCompleted ? "var(--color-success)" : "transparent"}`,
                            textAlign: "left", cursor: "pointer", color: "var(--color-text)",
                          }}
                        >
                          <div style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: "0.9rem", color: isSelected ? "var(--color-accent)" : "var(--color-text)" }}>
                            {gw.name}
                          </div>
                          {isCompleted ? (
                            <div style={{ textAlign: "right" }}>
                              <div style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: "1.15rem", lineHeight: 1, color: isSelected ? "var(--color-accent)" : "var(--color-text)" }}>
                                {gw.myScore}
                              </div>
                              <div style={{ fontSize: "0.55rem", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--color-text-dim)", marginTop: 1 }}>{t("scorePredictor.points")}</div>
                            </div>
                          ) : gw.fixtureCount > 0 ? (
                            <div style={{ textAlign: "right" }}>
                              {gw.predictedCount > 0 ? (
                                <span title={`${gw.predictedCount} of ${gw.fixtureCount} predicted`} style={{ fontSize: "0.75rem", color: gw.predictedCount === gw.fixtureCount ? "var(--color-success)" : "oklch(0.80 0.15 55)" }}>●</span>
                              ) : (
                                <span title="No predictions yet" style={{ fontSize: "0.75rem", color: "var(--color-danger)" }}>●</span>
                              )}
                            </div>
                          ) : null}
                        </button>
                      </React.Fragment>
                    );
                  })}
                </div>
                <div style={{ display: "flex", borderTop: "1px solid var(--color-border)", flexShrink: 0 }}>
                  <button
                    style={{ ...navBtnStyle, opacity: selectedGwIdx <= 0 ? 0.4 : 1 }}
                    disabled={selectedGwIdx <= 0}
                    onClick={() => selectedGwIdx > 0 && selectGw(visibleGameweeks[selectedGwIdx - 1].id)}
                  >
                    {t("scorePredictor.prevNav")}
                  </button>
                  <button
                    style={{ ...navBtnStyle, borderRight: "none", opacity: selectedGwIdx >= visibleGameweeks.length - 1 ? 0.4 : 1 }}
                    disabled={selectedGwIdx >= visibleGameweeks.length - 1}
                    onClick={() => selectedGwIdx < visibleGameweeks.length - 1 && selectGw(visibleGameweeks[selectedGwIdx + 1].id)}
                  >
                    {t("scorePredictor.nextNav")}
                  </button>
                </div>
              </div>
            );
          })()}

          {/* Fixture prediction card */}
          {(leagueStatus === "RUNNING" || leagueStatus === "COMPLETED") ? (
            <div style={{ ...card, padding: 0 }}>
              {/* GW header */}
              {(() => {
                const selGw = visibleGameweeks.find((g) => g.id === selectedGwId);
                const selIdx = visibleGameweeks.findIndex((g) => g.id === selectedGwId);
                const hasUnlocked = !isViewingMember && fixtures.some((f) => !f.locked);
                const hasPending = fixtures.some((f) => {
                  if (f.locked) return false;
                  const s = pendingScores[f.id];
                  return s && s.home !== null && s.away !== null;
                });
                const headerNavBtn: React.CSSProperties = {
                  background: "color-mix(in srgb, var(--color-accent) 12%, transparent)",
                  border: "1px solid color-mix(in srgb, var(--color-accent) 35%, transparent)",
                  padding: "0.25rem 0.55rem",
                  cursor: "pointer", color: "var(--color-accent)", fontSize: "1.1rem",
                  fontWeight: 700, lineHeight: 1, borderRadius: 6,
                };
                return selGw ? (
                  <div style={{
                    padding: "0.5rem 0.75rem",
                    borderBottom: "1px solid var(--color-border)",
                    display: "flex", alignItems: "center", gap: "0.5rem",
                  }}>
                    <span style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: "1rem", color: "var(--color-text)", flex: 1 }}>
                      {selGw.name}
                    </span>
                    {hasUnlocked && (
                      <button
                        style={{ ...primaryBtn, padding: "0.3rem 0.85rem", fontSize: "0.78rem", opacity: hasPending ? 1 : 0.4 }}
                        disabled={savingGw || !hasPending}
                        onClick={handleSaveGw}
                      >
                        {savingGw ? t("scorePredictor.savingLabel") : t("scorePredictor.saveLabel")}
                      </button>
                    )}
                    <button
                      style={{ ...headerNavBtn, opacity: selIdx <= 0 ? 0.3 : 1 }}
                      disabled={selIdx <= 0}
                      onClick={() => selIdx > 0 && selectGw(visibleGameweeks[selIdx - 1].id)}
                    >‹</button>
                    <button
                      style={{ ...headerNavBtn, opacity: selIdx >= visibleGameweeks.length - 1 ? 0.3 : 1 }}
                      disabled={selIdx >= visibleGameweeks.length - 1}
                      onClick={() => selIdx < visibleGameweeks.length - 1 && selectGw(visibleGameweeks[selIdx + 1].id)}
                    >›</button>
                  </div>
                ) : null;
              })()}
              {fixtures.length === 0 && (
                <div style={{ padding: "1.5rem", color: "var(--color-text-muted)", fontSize: "0.85rem" }}>{t("scorePredictor.noFixtures")}</div>
              )}
              {fixtures.map((fixture, idx) => {
                const isLast = idx === fixtures.length - 1;
                const pending = pendingScores[fixture.id] ?? { home: null, away: null };
                const actual = fixture.homeScore !== null && fixture.awayScore !== null
                  ? { homeScore: fixture.homeScore, awayScore: fixture.awayScore }
                  : null;
                const r = fixture.locked && actual && fixture.myPrediction
                  ? resultLabel(fixture.myPrediction, actual, SP_EXACT, SP_CORRECT)
                  : null;

                return (
                  <div key={fixture.id} style={{
                    display: "grid",
                    gridTemplateColumns: `1fr ${isMobile ? "120px" : "140px"} 1fr ${isMobile ? "44px" : "52px"}`,
                    alignItems: "center", gap: "0.4rem",
                    padding: "0.55rem 0.75rem",
                    borderBottom: isLast ? "none" : "1px solid var(--color-border-subtle)",
                    borderLeft: r ? `3px solid ${r.color}` : "3px solid transparent",
                  }}>
                    {/* Home */}
                    <div style={{ display: "flex", alignItems: "center", gap: "0.3rem", justifyContent: "flex-end", minWidth: 0 }}>
                      <span style={{
                        fontSize: "0.78rem", fontWeight: 600, color: "var(--color-text)", textAlign: "right",
                        ...(isMobile
                          ? { whiteSpace: "normal", overflow: "visible" }
                          : { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }),
                      }}>{fixture.homeClub.shortName ?? fixture.homeClub.name}</span>
                      <ClubBadge config={getClubBadge(fixture.homeClub.name)} size={20} title={fixture.homeClub.name} />
                    </div>

                    {/* Center: prediction (primary) / actual (secondary when locked) */}
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
                      {!fixture.locked && !isViewingMember ? (
                        <div style={{ display: "flex", alignItems: "center", gap: "0.25rem" }}>
                          <ScoreSelect
                            value={pending.home}
                            onChange={(v) => setPendingScores((prev) => ({ ...prev, [fixture.id]: { ...prev[fixture.id] ?? { home: null, away: null }, home: v } }))}
                            compact={isMobile}
                          />
                          <span style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: "1.05rem", letterSpacing: "0.03em", color: "var(--color-text-muted)", lineHeight: 1 }}>·</span>
                          <ScoreSelect
                            value={pending.away}
                            onChange={(v) => setPendingScores((prev) => ({ ...prev, [fixture.id]: { ...prev[fixture.id] ?? { home: null, away: null }, away: v } }))}
                            compact={isMobile}
                          />
                        </div>
                      ) : (
                        <>
                          {!fixture.myPrediction && !fixture.locked && isViewingMember ? (
                            <span style={{ fontSize: "0.62rem", color: "var(--color-text-dim)", fontStyle: "italic" }}>
                              {t("scorePredictor.predictionHidden")}
                            </span>
                          ) : (
                            <span style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: "1.05rem", letterSpacing: "0.03em", lineHeight: 1, color: fixture.myPrediction ? "var(--color-text)" : "var(--color-text-dim)" }}>
                              {fixture.myPrediction
                                ? `${fixture.myPrediction.homeScore}–${fixture.myPrediction.awayScore}`
                                : "–"}
                            </span>
                          )}
                          {actual && (
                            <span style={{ fontSize: "0.6rem", color: r ? r.color : "var(--color-text-dim)", fontWeight: 600 }}>
                              {actual.homeScore}–{actual.awayScore}
                            </span>
                          )}
                          {!actual && fixture.kickoffTime && (
                            <span style={{ fontSize: "0.56rem", color: "var(--color-text-dim)" }}>{formatKickoff(fixture.kickoffTime)}</span>
                          )}
                        </>
                      )}
                    </div>

                    {/* Away */}
                    <div style={{ display: "flex", alignItems: "center", gap: "0.3rem", minWidth: 0 }}>
                      <ClubBadge config={getClubBadge(fixture.awayClub.name)} size={20} title={fixture.awayClub.name} />
                      <span style={{
                        fontSize: "0.78rem", fontWeight: 600, color: "var(--color-text)",
                        ...(isMobile
                          ? { whiteSpace: "normal", overflow: "visible" }
                          : { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }),
                      }}>{fixture.awayClub.shortName ?? fixture.awayClub.name}</span>
                    </div>

                    {/* Points pill (locked) or kickoff (unlocked) */}
                    <div style={{ display: "flex", justifyContent: "flex-end" }}>
                      {fixture.locked && r ? (
                        <span style={{
                          fontFamily: "var(--font-display)", fontWeight: 800, fontSize: "0.88rem",
                          color: r.pts > 0 ? r.color : "var(--color-text-dim)",
                          background: r.pts > 0 ? `color-mix(in srgb, ${r.color} 12%, transparent)` : "var(--color-surface-raised)",
                          border: `1px solid ${r.pts > 0 ? r.color : "var(--color-border)"}`,
                          borderRadius: 6, padding: "0.15rem 0.45rem", lineHeight: 1.4,
                        }}>
                          {r.pts > 0 ? `+${r.pts}` : "0"}
                        </span>
                      ) : !fixture.locked && fixture.kickoffTime ? (
                        <span style={{ fontSize: "0.58rem", color: "var(--color-text-dim)", textAlign: "right", lineHeight: 1.3 }}>{formatKickoff(fixture.kickoffTime)}</span>
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div style={{ ...card, padding: "2rem", textAlign: "center", color: "var(--color-text-muted)", fontSize: "0.85rem", fontStyle: "italic" }}>
              {t("scorePredictor.predictionsUnavailable")}
            </div>
          )}
        </div>
      )}

      {/* ── PERFORMANCE TAB ── */}
      {activeTab === "performance" && (
        <div>
          {isMobile && data.myRank != null && data.memberCount != null && (
            <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap", marginBottom: "1rem" }}>
              <StatBox label={t("scorePredictor.rankLabel")} value={`#${data.myRank}`} sub={t("scorePredictor.ofCount", { count: data.memberCount })} borderColor="var(--color-accent)" />
            </div>
          )}
          {completedGws.length === 0 ? (
            <div style={{ ...card, padding: "2rem", textAlign: "center", color: "var(--color-text-muted)", fontSize: "0.85rem", fontStyle: "italic" }}>
              {t("scorePredictor.noDataYet")}
            </div>
          ) : (
            <div style={{ ...card, padding: "1rem 1.25rem" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "0.75rem" }}>
                <h2 style={{ ...panelHeading, margin: 0 }}>{t("scorePredictor.scoreProgression")}</h2>
                <div style={{ display: "flex", gap: "0.25rem" }}>
                  {(["per-gw", "cumulative"] as const).map((tab) => (
                    <button
                      key={tab}
                      onClick={() => setChartTab(tab)}
                      style={{
                        padding: "0.2rem 0.6rem", fontSize: "0.75rem", borderRadius: 6, border: "none",
                        cursor: "pointer", fontFamily: "var(--font-body)",
                        background: chartTab === tab ? "var(--color-accent)" : "var(--color-surface-alt)",
                        color: chartTab === tab ? "var(--color-btn-primary-text)" : "var(--color-text-muted)",
                      }}
                    >
                      {tab === "per-gw" ? t("scorePredictor.perGwTab") : t("scorePredictor.cumulativeTab")}
                    </button>
                  ))}
                </div>
              </div>

              {chartTab === "per-gw" ? (
                <ResponsiveContainer width="100%" height={180}>
                  <BarChart data={perGwData} margin={{ top: 4, right: 4, bottom: 4, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border-subtle)" vertical={false} />
                    <XAxis dataKey="name" tick={{ fontSize: 9, fill: "var(--color-text-dim)" }} axisLine={false} tickLine={false} interval={tickInterval} />
                    <YAxis tick={{ fontSize: 9, fill: "var(--color-text-dim)" }} axisLine={false} tickLine={false} domain={[0, "auto"]} allowDecimals={false} />
                    <Tooltip content={<SpBarTooltip />} cursor={false} />
                    <Bar dataKey="score" radius={[2, 2, 0, 0]} minPointSize={3}>
                      {perGwData.map((entry, index) => (
                        <Cell key={index} fill={entry.score > 0 ? "var(--color-accent)" : "var(--color-border)"} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <ResponsiveContainer width="100%" height={180}>
                  <AreaChart data={cumulativeData} margin={{ top: 4, right: 4, bottom: 4, left: 0 }}>
                    <defs>
                      <linearGradient id="sp-area-grad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="var(--color-accent)" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="var(--color-accent)" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border-subtle)" />
                    <XAxis dataKey="name" tick={{ fontSize: 9, fill: "var(--color-text-dim)" }} axisLine={false} tickLine={false} interval={cumulativeData.length > 9 ? Math.floor(cumulativeData.length / 5) : 0} />
                    <YAxis tick={{ fontSize: 9, fill: "var(--color-text-dim)" }} axisLine={false} tickLine={false} domain={[0, "auto"]} allowDecimals={false} />
                    <Tooltip contentStyle={tooltipStyle} formatter={(value: any) => [`${value} ${t("scorePredictor.points")}`, "Total"]} />
                    <Area type="monotone" dataKey="score" stroke="var(--color-accent)" strokeWidth={2} fill="url(#sp-area-grad)" dot={false} />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </div>
          )}
        </div>
      )}

    </div>
  );
}
